/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import https from 'https';
import getConfig from '../config';
import AnalyticsUtil from '../analytics/AnalyticsUtil';
import { getLogger } from '../utils/SearchLogger';
const ingestPropertiesToExclude = [
	'event.guid',
	'event.context_guid',
	'event.context_sequence',
	'event.user_guid',
	'event.visitor_guid',
	'event.mcid_guid',
	'event.build',
	'source.client_id',
	'env.fw.name',
	'env.fw.version',
	'env.com.name',
	'env.com.version',
	'env.svc.name',
	'env.svc.version',
	'env.api.name',
	'env.api.version',
];

class EventsService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * This method sends an event to the events table
	 * @param {array} eventInfo
	 */
	reportEvents(ingestEventInfo: Array<Record<string, any>>) {
		if (
			!AnalyticsUtil.optOutMachineLearning &&
			this.searchHostContext.featureFlags.includes('intents_server_tracking_search') &&
			Array.isArray(ingestEventInfo) &&
			ingestEventInfo.length > 0
		) {
			const eventsRequestPayload: any = [];
			ingestEventInfo.forEach((ingestEvent) => {
				const eventsArray = ingestEvent.events;
				const codeVersion = '1.2';

				/* istanbul ignore else */
				if (Array.isArray(eventsArray) && eventsArray.length > 0) {
					eventsArray.forEach((event) => {
						// copy the event data
						const eventInfo = Object.assign({}, event.data);

						// if referrer has access token, replace the event.referrer with the text 'login'
						if (
							eventInfo['event.referrer'] &&
							typeof eventInfo['event.referrer'] === 'string' &&
							eventInfo['event.referrer'].indexOf('token') > -1
						) {
							eventInfo['event.referrer'] = 'login';
						}

						// if url has access token, replace the event.url with the text 'login'
						if (
							eventInfo['event.url'] &&
							typeof eventInfo['event.url'] === 'string' &&
							eventInfo['event.url'].indexOf('token') > -1
						) {
							eventInfo['event.url'] = 'login';
						}

						// add code version # to the body
						eventInfo['code.version'] = codeVersion;
						// remove the properties we don't care about.
						ingestPropertiesToExclude.forEach((property) => {
							if (Object.prototype.hasOwnProperty.call(eventInfo, property)) {
								delete eventInfo[property];
							}
						});

						if (eventInfo['source.os_version'] === null) {
							eventInfo['source.os_version'] = '';
						}

						// make sure none of our properties are empty strings
						// since DynamoDB doesn't like those.
						for (const property in eventInfo) {
							if (eventInfo[property] === '') {
								delete eventInfo[property];
							}
						}

						// add this event to the event request payload array
						const eventRequest = {
							method: 'POST',
							body: {},
						};
						eventRequest.body = eventInfo;
						eventsRequestPayload.push(eventRequest);
					});
				}
			});

			// send the request to the server

			/* istanbul ignore else */
			if (eventsRequestPayload && eventsRequestPayload.length > 0) {
				this.performCreateEventRequest(eventsRequestPayload);
			}
		}
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: EventsService;
	searchHostContext: HostContext;

	constructor(searchHostContext: HostContext) {
		if (!EventsService.instance) {
			this.searchHostContext = searchHostContext;
			EventsService.instance = this;
		}

		return EventsService.instance;
	}

	/**
	 * This method posts an event to the intents server
	 * @param {object} bodyData event body
	 */
	performCreateEventRequest(bodyData: Array<Record<string, any>>): Promise<Record<string, any> | Error> {
		return new Promise((resolve) => {
			const postData = JSON.stringify(bodyData);
			const request = https.request(
				{
					hostname: getConfig(this.searchHostContext.environment, 'INTENTS_SERVER'),
					path: '/v1/users/me/events/batch',
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						'x-api-key': this.searchHostContext.hostInfo.apiKey,
						authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
					},
					// TODO: TS These needs to enabled
					// dataType: 'json',
					// withCredentials: false,
				},
				(res: any) => {
					if (res.statusCode >= 300) {
						getLogger().error('Events Service', 'Problem with Events request', res);
						const eventsServiceError = new Error('Events error');
						resolve(eventsServiceError);
					} else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						// https://jira.corp.adobe.com/browse/CCH-4427
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								resolve(JSON.parse(data));
							} catch (e) {
								getLogger().error(
									'Events Service',
									'Problem with parsing Events response',
									e.toString(),
								);
								const eventsServiceError = new Error('Events parse error');
								resolve(eventsServiceError);
							}
						});
					}
				},
			);
			request.on('error', (err) => {
				// There was a connection error of some sort
				getLogger().error('Events Service', 'Problem with Events request', err.toString());
				const eventsServiceError = new Error('Events error');
				resolve(eventsServiceError);
			});
			request.write(postData);
			request.end();
		});
	}
}

export default EventsService;
